import React, { useRef, useEffect } from 'react';
import MiniCalendar from './MiniCalendar';

/**
 * ListViewFilterBar - The second row of the table containing all dropdown filters.
 */
export default function ListViewFilterBar({
    statusFilter, setStatusFilter,
    dateRange, setDateRange,
    dateMode, setDateMode,
    workerFilter, setWorkerFilter,
    vehicleFilter, setVehicleFilter,
    kolonnenFilter, setKolonnenFilter,
    workerSearch, setWorkerSearch,
    vehicleSearch, setVehicleSearch,
    kolonneSearch, setKolonneSearch,
    openDropdown, setOpenDropdown,
    hidePast, setHidePast,
    allWorkers, allVehicles, kolonnen,
    STATUS_OPTIONS,
    isConflictDetectionEnabled,
    isReadOnly,
    isFilterActive,
    resetAllFilters
}) {
    const filterRowRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (filterRowRef.current && !filterRowRef.current.contains(event.target)) {
                setOpenDropdown(null);
                setWorkerSearch('');
                setVehicleSearch('');
                setKolonneSearch('');
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [setOpenDropdown, setWorkerSearch, setVehicleSearch, setKolonneSearch]);

    const toggleDropdown = (name) => setOpenDropdown(openDropdown === name ? null : name);
    const handleBackdropClick = (e) => { e.stopPropagation(); setOpenDropdown(null); };

    const toggleMultiFilter = (list, setList, value) => {
        if (list.includes(value)) setList(list.filter(item => item !== value));
        else setList([...list, value]);
    };

    const getStatusLabel = () => {
        if (statusFilter.length === 0) return 'Status: Keine';
        if (statusFilter.length === STATUS_OPTIONS.length) return 'Status: Alle';
        if (statusFilter.length === 1) return STATUS_OPTIONS.find(o => o.value === statusFilter[0])?.label;
        return `${statusFilter.length} Status`;
    };

    const getDateLabel = () => {
        if (dateMode === 'undated') return 'Undatiert';
        const hasRange = dateRange.start || dateRange.end;
        const formatDate = (dt) => {
            if (!dt) return '—';
            const d = new Date(dt);
            return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
        };
        if (hidePast) return 'Zukunft';
        if (!hasRange) return dateMode === 'combined' ? 'Datum: Alle' : 'Kalender';
        const s = formatDate(dateRange.start);
        const e = formatDate(dateRange.end);
        const rangeStr = s === e ? s : `${s} - ${e}`;
        return dateMode === 'combined' ? `${rangeStr} & Undat.` : rangeStr;
    };

    const getWorkerLabel = () => {
        if (workerFilter.length === allWorkers.length) return 'Mitarbeiter: Alle';
        if (workerFilter.length === 0) return 'Mitarbeiter: Keine';
        return `${workerFilter.length} Mitarb.`;
    };

    const getVehicleLabel = () => {
        if (vehicleFilter.length === allVehicles.length) return 'Fahrzeuge: Alle';
        if (vehicleFilter.length === 0) return 'Fahrzeuge: Keine';
        return `${vehicleFilter.length} Fzg.`;
    };

    const getKolonneLabel = () => {
        const totalOptions = kolonnen.length + 1;
        if (kolonnenFilter.length === totalOptions) return 'Kolonne: Alle';
        if (kolonnenFilter.length === 0) return 'Kolonne: Keine';
        return `${kolonnenFilter.length} Kolonnen`;
    };

    return (
        <tr className="leader-list-filter-row" ref={filterRowRef}>
            <th className="checkbox-col"></th>
            {isConflictDetectionEnabled && <th style={{ width: 40 }}></th>}
            {/* Status Filter */}
            <th>
                <div className="leader-list-filter-dropdown small">
                    <button className={`filter-dropdown-btn inline ${openDropdown === 'status' ? 'active' : ''}`} onClick={() => toggleDropdown('status')}>
                        <span>{getStatusLabel()}</span><span>▼</span>
                    </button>
                    {openDropdown === 'status' && (
                        <>
                            <div className="leader-list-backdrop" onClick={handleBackdropClick} />
                            <div className="filter-dropdown-menu" onClick={e => e.stopPropagation()}>
                                <div className="filter-dropdown-menu-list">
                                {STATUS_OPTIONS.map(opt => {
                                    const isChecked = statusFilter.includes(opt.value);
                                    return (
                                        <div key={opt.value} className="leader-filter-item-static" onClick={() => toggleMultiFilter(statusFilter, setStatusFilter, opt.value)}>
                                            <div className={`leader-custom-checkbox ${isChecked ? 'checked' : ''}`}>
                                                {isChecked && <span className="checkmark">✓</span>}
                                            </div>
                                            <span className="status-dot-mini" style={{ background: opt.color }}></span>
                                            <span className="filter-label">{opt.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="filter-actions-row">
                                <small className="filter-action-btn action-all" onClick={() => setStatusFilter(STATUS_OPTIONS.map(o => o.value))}>Alle</small>
                                <span style={{ margin: '0 5px', color: '#ccc' }}>|</span>
                                <small className="filter-action-btn action-none" onClick={() => setStatusFilter([])}>Keine</small>
                            </div>
                        </div>
                        </>
                    )}
                </div>
            </th>
            <th colSpan="2"></th>
            {/* Termin Filter */}
            <th>
                <div className="leader-list-filter-dropdown">
                    <button className={`filter-dropdown-btn inline ${openDropdown === 'date' ? 'active' : ''}`} onClick={() => toggleDropdown('date')}>
                        <span>{getDateLabel()}</span><span>▼</span>
                    </button>
                    {openDropdown === 'date' && (
                        <>
                            <div className="leader-list-backdrop" onClick={handleBackdropClick} />
                            <div className="filter-dropdown-menu date-picker-dropdown" onClick={e => e.stopPropagation()}>
                                <div className="export-segmented-control" style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
                                    <button className={`segment-btn ${dateMode === 'calendar' ? 'active' : ''}`} onClick={() => setDateMode('calendar')}>Kalender</button>
                                    <button className={`segment-btn ${dateMode === 'undated' ? 'active' : ''}`} onClick={() => setDateMode('undated')}>Undatiert</button>
                                    <button className={`segment-btn ${dateMode === 'combined' ? 'active' : ''}`} onClick={() => setDateMode('combined')}>Alles</button>
                                </div>
                                
                                <div style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                                    <div className="leader-filter-item-static" onClick={() => {
                                        const next = !hidePast;
                                        setHidePast(next);
                                        if (next) setDateRange({ start: null, end: null });
                                    }} style={{ margin: 0, padding: '4px 0' }}>
                                        <div className={`leader-custom-checkbox ${hidePast ? 'checked' : ''}`}>
                                            {hidePast && <span className="checkmark">✓</span>}
                                        </div>
                                        <span className="filter-label" style={{ fontWeight: 600, fontSize: '0.8rem' }}>Nur heute & Zukunft anzeigen</span>
                                    </div>
                                </div>
                                {dateMode !== 'undated' && (
                                    <div style={{ 
                                        opacity: hidePast ? 0.4 : 1, 
                                        pointerEvents: hidePast ? 'none' : 'auto',
                                        transition: 'opacity 0.2s' 
                                    }}>
                                        <MiniCalendar 
                                            start={dateRange.start} 
                                            end={dateRange.end} 
                                            onSelect={(s, e) => {
                                                setHidePast(false);
                                                setDateRange({ start: s, end: e });
                                            }} 
                                        />
                                    </div>
                                )}
                                <div className="filter-actions-row">
                                    <small className="filter-action-btn action-none" onClick={() => {
                                        setDateRange({ start: null, end: null });
                                        setDateMode('combined');
                                    }}>Zurücksetzen</small>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </th>
            {/* Kolonne Filter */}
            <th>
                <div className="leader-list-filter-dropdown">
                    <button className={`filter-dropdown-btn inline ${openDropdown === 'kolonne' ? 'active' : ''}`} onClick={() => toggleDropdown('kolonne')}>
                        <span>{getKolonneLabel()}</span><span>▼</span>
                    </button>
                    {openDropdown === 'kolonne' && (
                        <>
                            <div className="leader-list-backdrop" onClick={handleBackdropClick} />
                            <div className="filter-dropdown-menu" onClick={e => e.stopPropagation()}>
                                <div className="filter-dropdown-search-wrapper">
                                <input className="filter-dropdown-search-input" placeholder="Suchen..." value={kolonneSearch} onChange={e => setKolonneSearch(e.target.value)} autoFocus />
                            </div>
                            <div className="filter-dropdown-menu-list scrollable">
                                {!kolonneSearch && (
                                    <>
                                        <div className="leader-filter-item-static" onClick={() => toggleMultiFilter(kolonnenFilter, setKolonnenFilter, 'none')}>
                                            <div className={`leader-custom-checkbox ${kolonnenFilter.includes('none') ? 'checked' : ''}`}>
                                                {kolonnenFilter.includes('none') && <span className="checkmark">✓</span>}
                                            </div>
                                            <span className="filter-label" style={{ fontStyle: 'italic', color: '#64748b' }}>Ohne Kolonne</span>
                                        </div>
                                        <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />
                                    </>
                                )}
                                {kolonnen.filter(k => (k.internal_name || k.name).toLowerCase().includes(kolonneSearch.toLowerCase())).map(k => {
                                    const isChecked = kolonnenFilter.includes(k.id);
                                    return (
                                        <div key={k.id} className="leader-filter-item-static" onClick={() => toggleMultiFilter(kolonnenFilter, setKolonnenFilter, k.id)}>
                                            <div className={`leader-custom-checkbox ${isChecked ? 'checked' : ''}`}>
                                                {isChecked && <span className="checkmark">✓</span>}
                                            </div>
                                            <span className="filter-label">{k.internal_name || k.name}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="filter-actions-row">
                                <small className="filter-action-btn action-all" onClick={() => setKolonnenFilter([...kolonnen.map(k => k.id), 'none'])}>Alle</small>
                                <span style={{ margin: '0 5px', color: '#ccc' }}>|</span>
                                <small className="filter-action-btn action-none" onClick={() => setKolonnenFilter([])}>Keine</small>
                            </div>
                        </div>
                        </>
                    )}
                </div>
            </th>
            {/* Mitarbeiter Filter */}
            <th>
                <div className="leader-list-filter-dropdown">
                    <button className={`filter-dropdown-btn inline ${openDropdown === 'worker' ? 'active' : ''}`} onClick={() => toggleDropdown('worker')}>
                        <span>{getWorkerLabel()}</span><span>▼</span>
                    </button>
                    {openDropdown === 'worker' && (
                        <>
                            <div className="leader-list-backdrop" onClick={handleBackdropClick} />
                            <div className="filter-dropdown-menu" onClick={e => e.stopPropagation()}>
                                <div className="filter-dropdown-search-wrapper">
                                <input className="filter-dropdown-search-input" placeholder="Suchen..." value={workerSearch} onChange={e => setWorkerSearch(e.target.value)} autoFocus />
                            </div>
                            <div className="filter-dropdown-menu-list scrollable">
                                {allWorkers.filter(w => w.name.toLowerCase().includes(workerSearch.toLowerCase())).map(w => {
                                    const isChecked = workerFilter.includes(w.id);
                                    return (
                                        <div key={w.id} className="leader-filter-item-static" onClick={() => toggleMultiFilter(workerFilter, setWorkerFilter, w.id)}>
                                            <div className={`leader-custom-checkbox ${isChecked ? 'checked' : ''}`}>
                                                {isChecked && <span className="checkmark">✓</span>}
                                            </div>
                                            <span className="filter-label">{w.name}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="filter-actions-row">
                                <small className="filter-action-btn action-all" onClick={() => setWorkerFilter(allWorkers.map(w => w.id))}>Alle</small>
                                <span style={{ margin: '0 5px', color: '#ccc' }}>|</span>
                                <small className="filter-action-btn action-none" onClick={() => setWorkerFilter([])}>Keine</small>
                            </div>
                        </div>
                        </>
                    )}
                </div>
            </th>
            {/* Fahrzeug Filter */}
            <th>
                <div className="leader-list-filter-dropdown">
                    <button className={`filter-dropdown-btn inline ${openDropdown === 'vehicle' ? 'active' : ''}`} onClick={() => toggleDropdown('vehicle')}>
                        <span>{getVehicleLabel()}</span><span>▼</span>
                    </button>
                    {openDropdown === 'vehicle' && (
                        <>
                            <div className="leader-list-backdrop" onClick={handleBackdropClick} />
                            <div className="filter-dropdown-menu" onClick={e => e.stopPropagation()}>
                                <div className="filter-dropdown-search-wrapper">
                                <input className="filter-dropdown-search-input" placeholder="Suchen..." value={vehicleSearch} onChange={e => setVehicleSearch(e.target.value)} autoFocus />
                            </div>
                            <div className="filter-dropdown-menu-list scrollable">
                                {allVehicles.filter(v => (v.license_plate || v.name).toLowerCase().includes(vehicleSearch.toLowerCase())).map(v => {
                                    const isChecked = vehicleFilter.includes(v.id);
                                    return (
                                        <div key={v.id} className="leader-filter-item-static" onClick={() => toggleMultiFilter(vehicleFilter, setVehicleFilter, v.id)}>
                                            <div className={`leader-custom-checkbox ${isChecked ? 'checked' : ''}`}>
                                                {isChecked && <span className="checkmark">✓</span>}
                                            </div>
                                            <span className="filter-label">{v.license_plate || v.name}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="filter-actions-row">
                                <small className="filter-action-btn action-all" onClick={() => setVehicleFilter(allVehicles.map(v => v.id))}>Alle</small>
                                <span style={{ margin: '0 5px', color: '#ccc' }}>|</span>
                                <small className="filter-action-btn action-none" onClick={() => setVehicleFilter([])}>Keine</small>
                            </div>
                        </div>
                        </>
                    )}
                </div>
            </th>
            <th colSpan="2">
                {isFilterActive && (
                    <button 
                        className="leader-list-reset-btn" 
                        onClick={resetAllFilters}
                        title="Filter zurücksetzen"
                    >
                        🔄
                    </button>
                )}
            </th>
        </tr>
    );
}
